import {
  BarChart,
  Bar,
  Cell,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer,
  LabelList,
  ReferenceLine,
} from 'recharts'
import { Box, Typography } from '@mui/material'
import { useStore } from '~/state/useStore'
import type { WalkAbsoluteRow } from '~/api/endpoints/walks'
import type { Facet } from '~/state/useChartKnobsState'
import type { Group } from './dataPipeline'
import { formatAxisTick, formatCompactNumber } from '~/util/numbers'
import { renderBarValueLabel } from './chrome/barValueLabel'
import { MESSAGES } from './chrome/chartChrome'
import { AXIS_TICK, GRID_STROKE, TOOLTIP_NUM_STYLE, ZERO_LINE_STROKE } from './chrome/chartStyle'

interface BarPoint {
  dim: string
  [seriesKey: string]: string | number | undefined
}

interface Props {
  rows?: WalkAbsoluteRow[]
  points?: BarPoint[]
  groups?: Group[]
  facet?: Facet
  inactiveSeries?: string[]
  isPercentage?: boolean
  yDomain?: [number, number]
  hideYAxis?: boolean
  /** Formats the dim x-tick (and tooltip header) for display; `point.dim` identity stays raw. */
  xTickFormatter?: (value: string) => string
  /** base -> catalog-slot hex (spec D3), same resolver ChartCanvas feeds assignGroups
   *  (see ChartCanvas.tsx colorForBase). Reused here, keyed by dim VALUE instead of series
   *  base, for the single-series case below — the upper tiles already color dim values via
   *  this catalog, so a single-series bar-per-dim-value chart should match them. */
  colorForCategory?: (base: string) => string | undefined
}

export function pivot(rows: WalkAbsoluteRow[]): { points: BarPoint[]; seriesKeys: string[] } {
  const seriesKeys = new Set<string>()
  const byDim = new Map<string, BarPoint>()
  const distinctVts = new Set(rows.map((r) => r.value_type).filter((v): v is string => v != null))
  const showVt = distinctVts.size > 1
  for (const r of rows) {
    const dimKey = r.dims ? Object.values(r.dims).join(' / ') : 'all'
    const base = r.kpi_type ?? 'value'
    // ' · ' separator: kpi_type/dim values use '__' and '/', so no collision with a value_type suffix.
    const seriesKey = showVt && r.value_type != null ? `${base} · ${r.value_type}` : base
    seriesKeys.add(seriesKey)
    const existing = byDim.get(dimKey) ?? { dim: dimKey }
    // null (undefined ratio) → undefined so recharts renders no bar (gap), not a 0 bar.
    existing[seriesKey] = r.value ?? undefined
    byDim.set(dimKey, existing)
  }
  return { points: [...byDim.values()], seriesKeys: [...seriesKeys] }
}

// Show-Percentage normalisation: each row's series sum to 100% (semantics A — per-row pct of group total).
// v2 cross-read (`MMM-React/src/components/Charts/BarCharts/BarChart/index.jsx`, lines 43-48 `getShare`,
// 75-89 CustomTick, 126-150 renderCustomizedLabel) does NOT pre-transform `list`; v2 only swaps the tick/label
// text to show share-of-grand-total (% of all rows) or indexed-vs-first-row labels while bars stay on the
// absolute scale. Pre-transforming each row to sum=100% (semantics A) is the v3 spec position chosen at impl
// time — gives the user a 0..100 stacked-bar story per dim, which is the intuitive interpretation matching the
// "Show Percentage" menu label. v2's tick-only behaviour was deemed brittle to port verbatim because the v3
// renderer doesn't share v2's CustomTick/LabelList, and the spec calls out v2-verbatim only as an aim, not a
// strict mandate (see `docs/superpowers/specs/2026-05-07-batch-b-chart-rendering-design.md` §38, §286).
function normaliseToPercentage(points: BarPoint[], groups: Group[]): BarPoint[] {
  return points.map((p) => {
    const total = groups.reduce((acc, g) => acc + (Number(p[g.key]) || 0), 0)
    if (total === 0) return p
    const out: BarPoint = { dim: p.dim }
    for (const g of groups) {
      out[g.key] = Math.round(((Number(p[g.key]) || 0) / total) * 10000) / 100 // 2-decimal pct
    }
    return out
  })
}

export function AbsoluteBarChart(props: Props) {
  const palette = useStore((s) => s.personal.chipPalette)
  const fallbackPivot = props.rows ? pivot(props.rows) : { points: [], seriesKeys: [] }
  const points = props.points ?? fallbackPivot.points
  const groups: Group[] =
    props.groups ??
    fallbackPivot.seriesKeys.map((k, i) => ({
      key: k,
      label: k,
      color: { hex: palette[i % palette.length] ?? '#000', opacity: 1 },
    }))
  const facet: Facet = props.facet ?? 'bar'
  const inactive = props.inactiveSeries ?? []
  // Bound locally so the Tooltip labelFormatter closure narrows it without a non-null assertion.
  const xTickFmt = props.xTickFormatter
  const colorForCategory = props.colorForCategory
  const activeGroups = groups.filter((g) => !inactive.includes(g.key))
  // Bug-2 (Christoph): one kpi_type × N dim-value categories collapses to a single series —
  // every bar then shares that one series color instead of coloring by the group-by dim like
  // the upper tiles do. Gate on the FULL (pre-legend-toggle) `groups`, matching the SingleStatTile
  // guard's rationale in ChartCanvas — a single real series with >1 category is data-driven, not
  // a legend-toggle side effect. RankedDimBarChart.tsx:80-83 is the established per-Cell-color
  // idiom (CP bars); reused here. ChartLegend already drops its box below `groups.length < 2`
  // (ChartLegend.tsx, wave-4 rider #5) — this gate implies that condition, so the legend is
  // already suppressed for this case with no separate change needed.
  const isSingleSeriesMultiCategory = groups.length === 1 && points.length > 1
  const renderPoints = props.isPercentage ? normaliseToPercentage(points, groups) : points
  // Compact tooltip/bar-value-label numbers; percentage mode appends "%" (values pre-normalised
  // to 0–100). Wave-3 §3: the Σ/ø stat cells and BAR VALUE LABELS deliberately keep this.
  const fmtValue = (v: number | string): string =>
    typeof v !== 'number' ? String(v) : props.isPercentage ? `${v} %` : formatCompactNumber(v)
  // Wave-3 §3: AXIS ticks get the dedicated compact formatter — percentage branch unchanged.
  const fmtAxisValue = (v: number | string): string =>
    typeof v !== 'number' ? String(v) : props.isPercentage ? `${v} %` : formatAxisTick(v)

  if (points.length === 0) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography color="text.secondary">{MESSAGES.noDataForFilters}</Typography>
      </Box>
    )
  }

  return (
    <Box sx={{ width: '100%', height: 360 }}>
      <ResponsiveContainer>
        <BarChart data={renderPoints}>
          <CartesianGrid stroke={GRID_STROKE} vertical={false} />
          {(props.yDomain?.[0] ?? 0) < 0 ? (
            <ReferenceLine
              y={0}
              stroke={ZERO_LINE_STROKE}
              strokeWidth={2}
              label={{
                value: '0',
                position: 'insideBottomLeft',
                fontSize: 10,
                fill: ZERO_LINE_STROKE,
              }}
            />
          ) : null}
          <XAxis dataKey="dim" tickFormatter={xTickFmt} tick={AXIS_TICK} />
          <YAxis
            hide={props.hideYAxis}
            domain={props.yDomain}
            tickFormatter={fmtAxisValue}
            tick={AXIS_TICK}
          />
          <Tooltip
            formatter={(value) => fmtValue(value as number | string)}
            labelFormatter={xTickFmt ? (l) => xTickFmt(String(l)) : undefined}
            contentStyle={TOOLTIP_NUM_STYLE}
          />
          {activeGroups.map((g) => (
            <Bar
              key={g.key}
              dataKey={g.key}
              name={g.label}
              fill={g.color.hex}
              stackId={facet === 'stacked_bar' ? '1' : undefined}
              isAnimationActive={isSingleSeriesMultiCategory ? false : undefined}
            >
              {isSingleSeriesMultiCategory
                ? renderPoints.map((p) => (
                    <Cell
                      key={String(p.dim)}
                      fill={colorForCategory?.(String(p.dim)) ?? g.color.hex}
                    />
                  ))
                : null}
              {/* Row 7 (D3/D4): in-bar value labels; too-small bars go OUTSIDE by sign instead
                  of silent — except stacked segments, which keep the old suppression (an
                  outside label there would sit on the neighbouring segment). A single-segment
                  "stack" (one active series) has no neighbour to collide with, so the
                  suppression only guards REAL multi-segment stacks (FU-50). */}
              <LabelList
                dataKey={g.key}
                content={(p) =>
                  renderBarValueLabel(p as Parameters<typeof renderBarValueLabel>[0], fmtValue, {
                    allowOutside: facet !== 'stacked_bar' || activeGroups.length === 1,
                  })
                }
              />
            </Bar>
          ))}
        </BarChart>
      </ResponsiveContainer>
    </Box>
  )
}
